/* ═══════════════════════════════════════════════════════════════════════
   VMFS BUILDER CORE
   Shared framework for all VMFS product configurators.

   Responsibilities:
   - Read JSON config from the Liquid section
   - Render option UI (radio cards, swatches, toggles, dropdowns, multi)
   - Compute pricing + lead-time tier
   - Manage drawer, summary panel, lead-acknowledgment checkbox
   - Submit to Cloudflare Worker for draft order creation
   - Hand off SVG rendering to a registered machine-type renderer

   Renderer registry:
   Each machine-specific SVG file (snack-beverage, vape, elevator, coffee, …)
   calls VMFS.registerRenderer('renderer-name', { build, mapState }).
   The Liquid section declares which renderer to use via:
     <div data-vmfs-nectar data-renderer="snack-beverage">
   ═══════════════════════════════════════════════════════════════════════ */
(function(){
'use strict';

/* ─── Public registry ─────────────────────────────────────────────────── */
window.VMFS = window.VMFS || {};
window.VMFS.renderers = window.VMFS.renderers || {};
window.VMFS.registerRenderer = function(name, renderer){
  /* renderer is { build(state) → svgString,  mapState(rawState) → state } */
  if(!name || typeof name !== 'string') return;
  if(!renderer || typeof renderer.build !== 'function') return;
  window.VMFS.renderers[name] = renderer;
};

/* ─── Helpers ─────────────────────────────────────────────────────────── */
function unesc(s){return(s||"").replace(/&quot;/g,'"').replace(/&#39;/g,"'").replace(/&amp;/g,"&");}
function money(cents,sym){return`${sym}${(cents/100).toFixed(2)}`;}

function readConfig(root){
  const el=root.querySelector("[data-config]");
  if(!el)return null;
  try{return JSON.parse(unesc(el.textContent||""));}catch{return null;}
}

function meetsRules(state,rules){
  if(!rules||!rules.length)return true;
  return rules.every(r=>{
    const v=state[r.field],want=r.value;
    if(r.op==="==")return v===want;
    if(r.op==="!=")return v!==want;
    if(r.op==="in"){
      if(Array.isArray(want)){if(Array.isArray(v))return want.some(x=>v.includes(x));return want.includes(v);}
      if(Array.isArray(v))return v.includes(want);
      return v===want;
    }
    return true;
  });
}

/* ─── Init per builder instance ───────────────────────────────────────── */
function init(root){
  const config=readConfig(root);
  if(!config||!Array.isArray(config.groups))return;

  const sym          = config.currencySymbol||"$";
  const WORKER_URL   = config.workerUrl||"";
  const baseCents    = Number(config.basePriceCents||0);
  const state        = {};

  /* Renderer lookup — defaults to 'snack-beverage' for backwards compat */
  const rendererName = (root.dataset.renderer || config.renderer || 'snack-beverage');
  const renderer     = window.VMFS.renderers[rendererName];
  if(!renderer){
    console.warn(`[VMFS] No SVG renderer registered for '${rendererName}'. Make sure the matching renderer script is loaded.`);
  }

  const optionsWrap   = root.querySelector("[data-options]");
  const drawerOptions = root.querySelector("[data-drawer-options]");
  const propsWrap     = root.querySelector("[data-props]");
  const svgStage      = root.querySelector("[data-svg-stage]");
  const totalEl       = root.querySelector("[data-total-price]");
  const basePriceEl   = root.querySelector("[data-base-price]");
  const optsPriceEl   = root.querySelector("[data-options-price]");
  const summaryPanel  = root.querySelector("[data-summary-panel]");
  const summaryBody   = root.querySelector("[data-summary-body]");
  const summaryToggle = root.querySelector("[data-summary-toggle]");
  const summaryChevron= root.querySelector("[data-summary-chevron]");
  const leadCheckbox  = root.querySelector("[data-lead-checkbox]");
  const orderBtn      = root.querySelector("[data-order-btn]");

  function setStickyTop(){
    const el=root.querySelector(".vmfs-gallery-sticky");
    if(!el)return;
    const hdr=document.querySelector('header,.site-header,.shopify-section-header,[role="banner"]');
    el.style.top=(hdr?hdr.offsetHeight+20:20)+'px';
  }
  setStickyTop();
  window.addEventListener('resize',setStickyTop,{passive:true});

  root.querySelectorAll("[data-tabs] .vmfs-tab").forEach(tab=>{
    tab.addEventListener("click",()=>{
      root.querySelectorAll(".vmfs-tab").forEach(t=>t.classList.remove("is-active"));
      root.querySelectorAll(".vmfs-panel").forEach(p=>p.classList.remove("is-active"));
      tab.classList.add("is-active");
      const panel=root.querySelector(`[data-panel="${tab.dataset.tab}"]`);
      if(panel)panel.classList.add("is-active");
    });
  });

  const addonsDrawer  = root.querySelector("[data-drawer]");
  const drawerTrigger = root.querySelector("[data-addons-trigger]");
  root.querySelectorAll("[data-drawer-close]").forEach(el=>el.addEventListener("click",()=>addonsDrawer&&addonsDrawer.classList.remove("is-open")));
  if(drawerTrigger&&addonsDrawer)drawerTrigger.addEventListener("click",()=>addonsDrawer.classList.add("is-open"));

  if(summaryToggle&&summaryPanel){
    summaryToggle.addEventListener("click",()=>{
      const open=summaryPanel.classList.toggle("is-open");
      summaryToggle.setAttribute("aria-expanded",open?"true":"false");
      const lbl=summaryToggle.querySelector("[data-toggle-label]");
      if(lbl)lbl.textContent=open?"Hide Summary":"View Order Summary";
      if(summaryChevron)summaryChevron.style.transform=open?"rotate(180deg)":"rotate(0deg)";
    });
  }

  if(leadCheckbox&&orderBtn){
    leadCheckbox.addEventListener("change",()=>{orderBtn.disabled=!leadCheckbox.checked;});
  }

  function renderSvg(){
    if(!svgStage || !renderer) return;
    const svgState = (typeof renderer.mapState === 'function')
      ? renderer.mapState(state)
      : state;
    svgStage.style.transition = "opacity .15s ease, transform .15s ease";
    svgStage.style.opacity = "0";
    svgStage.style.transform = "scale(0.98)";
    setTimeout(()=>{
      svgStage.innerHTML = renderer.build(svgState);
      svgStage.style.opacity = "1";
      svgStage.style.transform = "scale(1)";
    }, 90);
  }

  function calcOptionsCents(){
    let sum=0;
    config.groups.forEach(g=>{
      if(!meetsRules(state,g.rules))return;
      const val=state[g.id];
      if(g.type==="multi"){
        if(Array.isArray(val))g.options.forEach(o=>{if(val.includes(o.value))sum+=Number(o.priceCents||0);});
      }else{
        const opt=g.options.find(o=>o.value===val);
        if(opt)sum+=Number(opt.priceCents||0);
      }
    });
    return sum;
  }

  function calcLeadTier(){
    let tier=0;
    config.groups.forEach(g=>{
      if(!meetsRules(state,g.rules))return;
      const val=state[g.id];
      if(g.type==="multi"){
        if(Array.isArray(val))g.options.forEach(o=>{if(val.includes(o.value)&&Number(o.leadTime||0)>tier)tier=Number(o.leadTime||0);});
      }else{
        const opt=g.options.find(o=>o.value===val);
        if(opt&&Number(opt.leadTime||0)>tier)tier=Number(opt.leadTime||0);
      }
    });
    return tier;
  }

  const leadTiers=config.leadTimes||{
    green: {label:"Standard Lead Time",days:"7-21 days",color:"#22c55e"},
    yellow:{label:"Medium Lead Time",   days:"21-45 days",color:"#eab308"},
    orange:{label:"Extended Lead Time", days:"45-75 days",color:"#f97316"}
  };
  const leadKeys=["green","yellow","orange"];

  function buildSummaryItems(){
    const items=[];
    config.groups.forEach(g=>{
      if(!meetsRules(state,g.rules))return;
      const val=state[g.id];
      if(g.type==="multi"){
        if(!Array.isArray(val))return;
        g.options.forEach(o=>{
          if(val.includes(o.value))items.push({group:g.label,label:o.label,priceCents:Number(o.priceCents||0)});
        });
      }else{
        const opt=g.options.find(o=>o.value===val);
        if(opt)items.push({group:g.label,label:opt.label,priceCents:Number(opt.priceCents||0)});
      }
    });
    return items;
  }

  function renderSummaryPanel(optionsCents,totalCents){
    if(!summaryBody)return;
    const items=buildSummaryItems();
    const paid=items.filter(i=>i.priceCents!==0);
    const inc =items.filter(i=>i.priceCents===0);

    let html="";
    if(paid.length){
      html+=`<div class="vmfs-sb-breakdown">`;
      html+=`<div class="vmfs-sb-row"><div class="vmfs-sb-row__label">Base Price</div><div class="vmfs-sb-row__val">${money(baseCents,sym)}</div></div>`;
      paid.forEach(i=>{
        const cls = i.priceCents>0?'vmfs-sb-add':'vmfs-sb-sub';
        const sign= i.priceCents>0?'+':'';
        html+=`<div class="vmfs-sb-row vmfs-sb-row--opt">
          <div class="vmfs-sb-row__label">
            <span class="vmfs-sb-row__group">${i.group}</span>
            <span class="vmfs-sb-row__opt-val">${i.label}</span>
          </div>
          <div class="vmfs-sb-row__val ${cls}">${sign}${money(Math.abs(i.priceCents),sym)}</div>
        </div>`;
      });
      html+=`<div class="vmfs-sb-divider"></div>`;
      html+=`<div class="vmfs-sb-row vmfs-sb-row--total"><div class="vmfs-sb-row__label">Total</div><div class="vmfs-sb-total">${money(totalCents,sym)}</div></div>`;
      html+=`</div>`;
    }else{
      html+=`<p class="vmfs-sb-empty">No paid add-ons selected. Base price: ${money(baseCents,sym)}</p>`;
    }

    if(inc.length){
      html+=`<div class="vmfs-sb-included"><div class="vmfs-sb-included__title">Included Selections</div><div class="vmfs-sb-included__list">`;
      inc.forEach(i=>{
        html+=`<div class="vmfs-sb-included__item">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>
          <span><strong>${i.group}:</strong> ${i.label}</span>
        </div>`;
      });
      html+=`</div></div>`;
    }
    summaryBody.innerHTML=html;
  }

  function priceBadge(cents){
    const c=Number(cents||0);
    if(c===0)return `<span class="vmfs-opt-price vmfs-opt-price--inc">Included</span>`;
    if(c>0) return `<span class="vmfs-opt-price vmfs-opt-price--add">+${money(c,sym)}</span>`;
    return `<span class="vmfs-opt-price vmfs-opt-price--sub">${money(c,sym)}</span>`;
  }

  function renderOption(g,container){
    const groupDiv=document.createElement("div");
    groupDiv.className="vmfs-option";
    const lbl=`<div class="vmfs-option-label">
      <span>${g.label||""}</span>
      ${g.hint?`<span class="vmfs-hint" tabindex="0">?<span class="vmfs-tooltip">${g.hint}</span></span>`:""}
    </div>`;

    if(g.display==="radio"){
      groupDiv.innerHTML=lbl+`<div class="vmfs-radio-group" data-group="${g.id}"></div>`;
      const cont=groupDiv.querySelector("[data-group]");
      g.options.forEach(o=>{
        const sel=state[g.id]===o.value;
        const card=document.createElement("label");
        card.className=`vmfs-radio-card${sel?" is-selected":""}`;
        card.innerHTML=`
          <div class="vmfs-radio-card__header">
            ${o.badge?`<span class="vmfs-radio-badge">${o.badge}</span>`:"<span></span>"}
            <span class="vmfs-radio-circle${sel?" is-checked":""}">${sel?'<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>':""}</span>
          </div>
          ${o.image?`<div class="vmfs-radio-img"><img src="${o.image}" alt="${o.label||""}"></div>`:""}
          <div class="vmfs-radio-card__body">
            <div class="vmfs-radio-title">${o.label||""}</div>
            ${o.bullets?`<ul class="vmfs-radio-bullets">${o.bullets.map(b=>`<li>${b}</li>`).join("")}</ul>`:""}
          </div>
          <div class="vmfs-radio-card__footer">${priceBadge(o.priceCents)}</div>`;
        card.addEventListener("click",()=>{state[g.id]=o.value;renderAll();});
        cont.appendChild(card);
      });
    }else if(g.display==="swatch"){
      groupDiv.innerHTML=lbl+`<div class="vmfs-pill-group" data-group="${g.id}"></div>`;
      const cont=groupDiv.querySelector("[data-group]");
      g.options.forEach(o=>{
        const sel=state[g.id]===o.value;
        const pill=document.createElement("label");
        pill.className=`vmfs-pill${sel?" is-selected":""}`;
        pill.innerHTML=`
          <span class="vmfs-pill-label">${o.label||""}</span>
          <span class="vmfs-pill-sub">${priceBadge(o.priceCents)}</span>`;
        pill.addEventListener("click",()=>{state[g.id]=o.value;renderAll();});
        cont.appendChild(pill);
      });
    }else if(g.display==="switch"){
      groupDiv.innerHTML=lbl+`<div class="vmfs-toggle-group" data-group="${g.id}"></div>`;
      const cont=groupDiv.querySelector("[data-group]");
      g.options.forEach(o=>{
        const sel=state[g.id]===o.value;
        const row=document.createElement("label");
        row.className=`vmfs-toggle-row${sel?" is-selected":""}`;
        row.innerHTML=`
          <div class="vmfs-toggle-info">
            ${o.image?`<img class="vmfs-toggle-img" src="${o.image}" alt="">`:""}
            <div><div class="vmfs-toggle-name">${o.label||""}</div>${o.desc?`<div class="vmfs-toggle-desc">${o.desc}</div>`:""}</div>
          </div>
          <div class="vmfs-toggle-right">
            <div class="vmfs-toggle-price">${priceBadge(o.priceCents)}</div>
            <div class="vmfs-ios-toggle${sel?" is-on":""}"><span class="vmfs-ios-knob"></span></div>
          </div>`;
        row.addEventListener("click",()=>{state[g.id]=o.value;renderAll();});
        cont.appendChild(row);
      });
    }else if(g.type==="multi"){
      groupDiv.innerHTML=lbl+`<div class="vmfs-toggle-group" data-group="${g.id}"></div>`;
      const cont=groupDiv.querySelector("[data-group]");
      g.options.forEach(o=>{
        const sel=Array.isArray(state[g.id])&&state[g.id].includes(o.value);
        const row=document.createElement("label");
        row.className=`vmfs-toggle-row${sel?" is-selected":""}`;
        row.innerHTML=`
          <div class="vmfs-toggle-info">
            ${o.image?`<img class="vmfs-toggle-img" src="${o.image}" alt="">`:""}
            <div><div class="vmfs-toggle-name">${o.label||""}</div>${o.desc?`<div class="vmfs-toggle-desc">${o.desc}</div>`:""}</div>
          </div>
          <div class="vmfs-toggle-right">
            <div class="vmfs-toggle-price">${priceBadge(o.priceCents)}</div>
            <div class="vmfs-ios-toggle${sel?" is-on":""}"><span class="vmfs-ios-knob"></span></div>
          </div>`;
        row.addEventListener("click",()=>{
          if(!Array.isArray(state[g.id]))state[g.id]=[];
          const idx=state[g.id].indexOf(o.value);
          if(idx>=0)state[g.id].splice(idx,1);else state[g.id].push(o.value);
          renderAll();
        });
        cont.appendChild(row);
      });
    }else{
      groupDiv.innerHTML=lbl+`<div class="vmfs-select-wrap"><select data-group="${g.id}" class="vmfs-select"></select><svg class="vmfs-select-arrow" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2"><path d="M5 8l5 5 5-5"/></svg></div>`;
      const select=groupDiv.querySelector("select");
      g.options.forEach(o=>{
        const opt=document.createElement("option");
        opt.value=o.value;
        const ps=Number(o.priceCents||0);
        opt.textContent=`${o.label||""}${ps>0?` (+${money(ps,sym)})`:ps<0?` (${money(ps,sym)})`:""}`;
        if(state[g.id]===o.value)opt.selected=true;
        select.appendChild(opt);
      });
      select.addEventListener("change",()=>{state[g.id]=select.value;renderAll();});
    }
    container.appendChild(groupDiv);
  }

  function renderOptions(){
    optionsWrap.innerHTML="";
    drawerOptions.innerHTML="";
    config.groups.filter(g=>g.section==="main").forEach(g=>{if(meetsRules(state,g.rules))renderOption(g,optionsWrap);});
    config.groups.filter(g=>g.section==="addon").forEach(g=>{if(meetsRules(state,g.rules))renderOption(g,drawerOptions);});
  }

  function renderPricing(){
    const optionsCents=calcOptionsCents();
    const totalCents=baseCents+optionsCents;
    if(basePriceEl) basePriceEl.textContent=money(baseCents,sym);
    if(optsPriceEl) optsPriceEl.textContent=money(optionsCents,sym);
    if(totalEl)     totalEl.textContent=money(totalCents,sym);

    const tier=calcLeadTier();
    const tierData=leadTiers[leadKeys[tier]];
    const dotEl  =root.querySelector("[data-lead-dot]");
    const labelEl=root.querySelector("[data-lead-label]");
    const daysEl =root.querySelector("[data-lead-days]");
    if(dotEl){dotEl.style.background=tierData.color;dotEl.style.boxShadow=`0 0 0 4px ${tierData.color}30`;}
    if(labelEl)labelEl.textContent=tierData.label;
    if(daysEl) daysEl.textContent=tierData.days;

    propsWrap.innerHTML="";
    Object.keys(state).forEach(k=>{
      const v=state[k];if(v==null)return;
      const inp=document.createElement("input");
      inp.type="hidden";inp.name=`properties[${k}]`;
      inp.value=Array.isArray(v)?v.join(", "):String(v);
      propsWrap.appendChild(inp);
    });

    renderSummaryPanel(optionsCents,totalCents);
  }

  function renderAll(){renderOptions();renderPricing();renderSvg();}

  if(Array.isArray(config.defaults)){
    config.defaults.forEach(d=>{if(d&&d.id)state[d.id]=d.value;});
  }
  renderAll();

  const form=root.querySelector("[data-vmfs-form]");
  if(form){
    form.addEventListener("submit",async function(e){
      e.preventDefault();
      if(!WORKER_URL){form.submit();return;}

      const optionsCents=calcOptionsCents();
      const totalCents=baseCents+optionsCents;
      const tier=leadTiers[leadKeys[calcLeadTier()]];

      const properties={};
      config.groups.forEach(g=>{
        if(!meetsRules(state,g.rules))return;
        const val=state[g.id];
        if(g.type==="multi"){
          if(Array.isArray(val)&&val.length){
            const labels=g.options.filter(o=>val.includes(o.value)).map(o=>o.label);
            if(labels.length)properties[g.label]=labels.join(", ");
          }
        }else{
          const opt=g.options.find(o=>o.value===val);
          if(opt)properties[g.label]=opt.label;
        }
      });
      properties["Base Price"]          = money(baseCents,sym);
      properties["Options Price"]       = money(optionsCents,sym);
      properties["Total Price"]         = money(totalCents,sym);
      properties["Estimated Lead Time"] = `${tier.label}: ${tier.days}`;

      const btn=form.querySelector("[data-order-btn]");
      const origHTML=btn?btn.innerHTML:"Add to Cart";

      try{
        if(btn){
          btn.disabled=true;
          btn.innerHTML=`<svg class="vmfs-btn-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg> Preparing checkout…`;
        }
        const res=await fetch(WORKER_URL,{
          method:"POST",
          headers:{"Content-Type":"application/json"},
          body:JSON.stringify({
            totalCents, baseCents, optionsCents, properties,
            variantId: form.querySelector('[name="id"]')?.value||null,
            currencySymbol: sym
          })
        });
        const data=await res.json();
        if(!res.ok||!data.checkoutUrl)throw new Error(data.error||"Could not create checkout");
        window.location.href=data.checkoutUrl;
      }catch(err){
        console.error("[VMFS] Checkout error:",err);
        alert("There was a problem creating your order: "+err.message);
        if(btn){btn.disabled=false;btn.innerHTML=origHTML;}
      }
    });
  }
}

document.addEventListener("DOMContentLoaded",()=>{
  document.querySelectorAll("[data-vmfs-nectar]").forEach(init);
});
})();
